import ErrorBoundary from "components/Error/errorBoundary";
import {PolIcon} from "components/PolIcon/policon";
import React, {useCallback, useMemo, useState} from 'react';
import {DragDropContext} from "react-beautiful-dnd";
import {useFormContext} from "react-hook-form";
import ReactTooltip from "react-tooltip";
import {getLSItem} from "v4/services/localStorage.service";
import QuotesProductsTableAttributesPopup
    from "v4/features/front/products/components/QuotesProductsDetails/components/QuotesProductsTable/components/QuotesProductsTableAttributesPopup/QuotesProductsTableAttributesPopup";
import QuotesProductsTableSection
    from "v4/features/front/products/components/QuotesProductsDetails/components/QuotesProductsTable/components/QuotesProductsTableSection/QuotesProductsTableSection";

import 'v4/features/front/products/components/QuotesProductsDetails/components/QuotesProductsTable/QuotesProductsTable.scss';
import {
    useQuotesProductsDetailsContext
} from "v4/features/front/products/components/QuotesProductsDetails/contexts/QuotesProductsDetailsContext/QuotesProductsDetailsContext";
import {usePolTranslation} from "v4/hooks/usePolTranslation";
import {trackQuoteAttributes} from "v4/services/debug/quoteAttributesSentry";
import {useQuoteTabContext} from "v4/pages/front/Prospect/components/ProspectTabsSwitcher/contexts/QuoteTabContext.jsx";
import ProductVoter from "v4/voters/productVoter";

export default function QuotesProductsTable({
                                                addLine,
                                                togglePopup,
                                                isShow,
                                                isQuoteFetchLoading,
                                            }) {
    const {t} = usePolTranslation();
    const {canProductShowList} = ProductVoter();

    const {
        quoteLinesMethods: {fields, move, update},
        sectionsNames,
        addSection,
    } = useQuotesProductsDetailsContext();

    const defaultQuoteSections = useMemo(() => getLSItem('user')?.defaultQuoteSections?.['hydra:member'] ?? [], []);
    const autocompleteOptions = useMemo(() => defaultQuoteSections.reduce((prev, {label}) => {
        if (!sectionsNames.some(({name}) => name.split('|')[1] === label)) {
            return [...prev, label];
        }

        return prev;
    }, []), [defaultQuoteSections, sectionsNames]);

    const [quoteLineAttributesFor, setQuoteLineAttributesFor] = useState(null);
    const {toggleCheckCustomerEventService, reofferConfig, openReoffer} = useQuoteTabContext();

    const isReofferVisible = isShow && !!reofferConfig?.isVisible && typeof openReoffer === 'function';

    const editAttributesFor = useCallback((index) => {
        setQuoteLineAttributesFor({
            index: index,
            productId: fields[index].quoteLineInfo.product.id
        });
    }, [fields]);

    const closePopup = () => {
        setQuoteLineAttributesFor(null);
        toggleCheckCustomerEventService();
    }

    const {getValues} = useFormContext();
    const onDragEnd = useCallback((result) => {
        const {source, destination, draggableId} = result;

        if (!destination || (source.droppableId === destination.droppableId && source.index === destination.index)) return;

        const from = fields.findIndex(({id}) => id === draggableId);
        const sectionNameDestination = sectionsNames.find(({id}) => id === destination.droppableId).name;

        // using getValues instead of fields because fields are not always updated
        const destinationSectionIndexes = getValues('quoteLines').reduce((acc, {sectionName}, index) => {
            if (index !== from && (sectionName ?? '') === sectionNameDestination) acc.push(index);

            return acc;
        }, []);
        const to = getMoveTarget(from, destinationSectionIndexes, destination.index);
        const line = getValues(`quoteLines.${from}`);

        // move keeps react-hook-form's registered fields aligned with their line, replace does not (PV-1789)
        move(from, to);
        if ((line.sectionName ?? '') !== sectionNameDestination) {
            update(to, {...line, sectionName: sectionNameDestination});
        }

        trackQuoteAttributes('drag:move', getValues('quoteLines'), {from, to, sectionNameDestination});
    }, [move, update, sectionsNames, fields, getValues]);

    return (
        <>
            <div className={`quotes-products__table${isShow ? ' is-show' : ''}`}>
                <div className={`quotes-products__thead${fields.length === 0 ? ' empty' : ''}`}>
                    {
                        fields.length > 0
                            ? <>
                                <div className="products__thead__img"></div>
                                <div className="products__thead__optional">
                                    {t('optional')}
                                    <span data-tip={t('quotes_products_optional_help')} data-for="optional"><PolIcon
                                        icon="question"/></span>
                                    <ReactTooltip id="optional" effect="solid"/>
                                </div>
                                <div className="products__thead__desc">{t('designation')}</div>
                                {/*isShow && <div className="products__thead__attributes">{t('product_attributes')}</div>*/}
                                <div className="products__thead__price">{t('quotes_unit_price')}</div>
                                <div className="products__thead__quantity">{t('quantity')}</div>
                                <div className="products__thead__discount">{t('discount')}</div>
                                <div className="products__thead__vat">{t('vat')}</div>
                                <div className="products__thead__total">{t('excl_taxes_amount')}</div>
                            </>
                            : (
                                <div className="quotes-products__no-products">
                                    <div>
                                        {t('no_quote_line')}
                                        {isQuoteFetchLoading && <PolIcon icon={'circle-notch'}/>}
                                    </div>
                                </div>
                            )
                    }
                    {
                        !isShow && !isQuoteFetchLoading && (
                            <div className="products__thead__actions">
                                <div className="quotes-products__actions">
                                    <button type="button" onClick={addLine}>
                                        <PolIcon icon="plus"/>
                                    </button>
                                    {canProductShowList() &&
                                        <button type="button" onClick={togglePopup}>
                                            <PolIcon icon="search-plus"/>
                                        </button>
                                    }
                                </div>
                            </div>
                        )
                    }
                    {
                        isReofferVisible && (
                            <div className="products__thead__actions">
                                <button type="button"
                                        className="quotes-products__reoffer-btn"
                                        onClick={() => openReoffer(reofferConfig.quoteId)}
                                        data-for="reoffer" data-tip={t('refresh_quote')}>
                                    <PolIcon icon="refresh"/>
                                </button>
                                <ReactTooltip id="reoffer" effect="solid"/>
                            </div>
                        )
                    }
                </div>
                <DragDropContext onDragEnd={onDragEnd}>
                    <div className="quotes-products__tbody">
                        {sectionsNames.map(({name, id, isDefault}, sectionIndex) => (
                            <QuotesProductsTableSection key={id}
                                                        isShow={isShow}
                                                        name={name}
                                                        id={id}
                                                        sectionIndex={sectionIndex}
                                                        isDefault={isDefault}
                                                        editAttributesFor={editAttributesFor}/>
                        ))}
                    </div>
                </DragDropContext>
                {!isShow && autocompleteOptions.length > 0 && (
                    <datalist id="quoteSections">
                        {autocompleteOptions.map((option, index) => (
                            <option key={index} value={option}/>
                        ))}
                    </datalist>
                )}
            </div>
            {!isShow && (
                <div className="quotes-products__add-section">
                    <button type="button"
                            disabled={sectionsNames.some(({isDefault}) => isDefault)}
                            onClick={addSection}>
                        <PolIcon icon="plus"/>
                        {t('new_section')}
                    </button>
                </div>
            )}
            {quoteLineAttributesFor !== null &&
                <ErrorBoundary>
                    <QuotesProductsTableAttributesPopup quoteLineAttributesFor={quoteLineAttributesFor}
                                                        closePopup={closePopup}
                                                        isShow={isShow}/>
                </ErrorBoundary>
            }
        </>
    )
}

function getMoveTarget(from, destinationSectionIndexes, destinationIndex) {
    const anchor = destinationSectionIndexes[destinationIndex];
    if (anchor !== undefined) return anchor > from ? anchor - 1 : anchor;

    const last = destinationSectionIndexes[destinationSectionIndexes.length - 1];
    if (last !== undefined) return last > from ? last : last + 1;

    return from;
}
